import React, { ReactNode } from 'react';
import { Analytics } from '@vercel/analytics/react';
import {
  Dumbbell,
  Sun,
  Moon,
  Settings,
  LogOut,
  Calendar,
  TrendingUp,
  ChevronRight,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import type { TabType } from '../types';
import { OfflineBanner } from './OfflineBanner';

interface LayoutProps {
  children: ReactNode;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  showSettings: boolean;
  setShowSettings: (show: boolean) => void;
  dataLoading: boolean;
  isDemoMode: boolean;
  hasActivePlan: boolean;
  isOffline: boolean;
  pendingCount: number;
}

export function Layout({
  children,
  activeTab,
  setActiveTab,
  showSettings,
  setShowSettings,
  dataLoading,
  isDemoMode,
  hasActivePlan,
  isOffline,
  pendingCount,
}: LayoutProps) {
  const { darkMode, toggleDarkMode, theme } = useTheme();
  const { handleLogout } = useAuth();
  const shellBackdrop = darkMode
    ? 'bg-gradient-to-b from-[#12100d] via-[#12100d]/92 to-[#12100d]/0'
    : 'bg-gradient-to-b from-[#f2ece2] via-[#f2ece2]/92 to-[#f2ece2]/0';
  const shellSurface = darkMode
    ? 'bg-[#16130f]/88 border-stone-800/90 shadow-[0_24px_80px_-48px_rgba(0,0,0,0.85)]'
    : 'bg-[#f8f3eb]/88 border-white/70 shadow-[0_24px_80px_-48px_rgba(120,83,49,0.35)]';
  const tabIdle = darkMode
    ? 'text-stone-400 hover:text-stone-100 hover:bg-[#241f19]'
    : 'text-stone-600 hover:text-stone-950 hover:bg-white/80';
  const tabActive = darkMode
    ? 'bg-gradient-to-r from-[#d97745] to-[#bc5f31] text-white shadow-[0_18px_36px_-24px_rgba(217,119,69,0.9)]'
    : 'bg-gradient-to-r from-[#b4532a] to-[#d97745] text-white shadow-[0_18px_36px_-24px_rgba(180,83,42,0.85)]';
  const utilityButton = darkMode
    ? 'border-stone-800 bg-[#221d18] text-stone-300 hover:bg-[#2b251f] hover:text-stone-100'
    : 'border-white/80 bg-white/78 text-stone-700 hover:bg-white hover:text-stone-950';

  const tabs = [
    {
      id: 'routines' as TabType,
      label: 'Rutinas',
      mobileLabel: 'Plan',
      icon: Dumbbell,
    },
    {
      id: 'workout' as TabType,
      label: hasActivePlan ? 'Hoy' : 'Entrenar',
      mobileLabel: 'Hoy',
      icon: Calendar,
    },
    {
      id: 'progress' as TabType,
      label: 'Progreso',
      mobileLabel: 'Progreso',
      icon: TrendingUp,
    },
  ];

  return (
    <div
      className={`min-h-screen ${theme.bg} ${theme.text} transition-colors duration-200`}
    >
      <div
        className={`fixed top-0 left-0 right-0 z-40 pointer-events-none ${shellBackdrop}`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className='max-w-6xl mx-auto px-3 sm:px-4 pt-3 sm:pt-4'>
          <div className='pointer-events-auto'>
            <div className={`animate-rise-in rounded-[28px] border px-3 py-3 sm:px-4 sm:py-4 backdrop-blur-xl ${shellSurface}`}>
            <div className='flex items-center justify-between gap-3 mb-2 sm:mb-3'>
              <div className='min-w-0'>
                <div className='flex items-center gap-3'>
                  <div
                    className={`flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-2xl border ${
                      darkMode
                        ? 'border-[#5a3925] bg-[#261a13] text-[#f2c7ae]'
                        : 'border-[#dfb79d] bg-[#f2e1d2] text-[#8e3f1d]'
                    }`}
                  >
                    <Dumbbell className='w-5 h-5' />
                  </div>
                  <div className='min-w-0'>
                    <h1 className='text-lg sm:text-2xl font-black tracking-[-0.04em] truncate'>
                      Groso.app
                    </h1>
                  </div>
                </div>
              </div>
              <div className='flex gap-2 shrink-0'>
                <button
                  onClick={toggleDarkMode}
                  className={`pressable rounded-2xl border p-2 sm:p-2.5 ${utilityButton}`}
                  aria-label='Toggle dark mode'
                >
                  {darkMode ? (
                    <Sun className='w-5 h-5' />
                  ) : (
                    <Moon className='w-5 h-5' />
                  )}
                </button>
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className={`pressable rounded-2xl border p-2 sm:p-2.5 ${utilityButton}`}
                  aria-label='Settings'
                >
                  <Settings className='w-5 h-5' />
                </button>
                <button
                  onClick={handleLogout}
                  className={`pressable rounded-2xl border p-2 sm:p-2.5 ${
                    darkMode
                      ? 'border-[#5a3925] bg-[#261a13] text-[#f2b89d] hover:bg-[#311f16] hover:text-white'
                      : 'border-[#ebc0a9] bg-[#fff1e8] text-[#9f4320] hover:bg-[#ffdecf] hover:text-[#6f2810]'
                  }`}
                  aria-label='Logout'
                >
                  <LogOut className='w-5 h-5' />
                </button>
              </div>
            </div>

            <div className={`rounded-3xl border p-1.5 ${darkMode ? 'border-stone-800 bg-[#130f0c]' : 'border-white/80 bg-white/55'}`}>
              <div className='grid grid-cols-3 gap-1.5'>
                {tabs.map(({ id, label, mobileLabel, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`pressable flex min-w-0 items-center justify-center sm:justify-between rounded-[20px] px-2 py-2.5 sm:px-3 sm:py-3 text-center sm:text-left ${
                      activeTab === id ? `${tabActive} animate-pulse-border` : tabIdle
                    }`}
                  >
                    <div className='flex min-w-0 flex-col items-center gap-1.5 sm:flex-row sm:gap-2.5'>
                      <div className={`rounded-2xl p-2 ${activeTab === id ? 'bg-white/15' : darkMode ? 'bg-[#201a15]' : 'bg-[#f1ebe4]'}`}>
                        <Icon className='w-4 h-4 sm:w-5 sm:h-5' />
                      </div>
                      <div className='min-w-0'>
                        <p className='truncate text-[11px] leading-none sm:text-base sm:leading-tight font-semibold'>
                          <span className='sm:hidden'>{mobileLabel}</span>
                          <span className='hidden sm:inline'>{label}</span>
                        </p>
                        <p className='sr-only'>
                          {label}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className={`hidden sm:block w-4 h-4 shrink-0 ${activeTab === id ? 'opacity-80' : 'opacity-35'}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
          </div>
          <div
            className={`h-10 sm:h-12 ${
              darkMode
                ? 'bg-gradient-to-b from-[#16130f]/40 to-transparent'
                : 'bg-gradient-to-b from-[#f8f3eb]/45 to-transparent'
            }`}
          />
        </div>
      </div>

      <div
        className='max-w-6xl mx-auto px-3 sm:px-4 pt-[calc(env(safe-area-inset-top)+11.5rem)] sm:pt-[calc(env(safe-area-inset-top)+12rem)] pb-[calc(env(safe-area-inset-bottom)+2rem)]'
      >
        {isDemoMode && !dataLoading && (
          <div
            className={`animate-rise-in mt-4 mb-5 rounded-[24px] border px-4 py-4 text-sm text-center ${
              darkMode
                ? 'border-amber-900/80 bg-[#2b2013] text-amber-100'
                : 'border-amber-300 bg-[#f7e5c8] text-amber-950'
            }`}
          >
            <span className='font-semibold tracking-[0.02em]'>Modo demo</span>
            {' '}· datos de ejemplo. Creá una rutina para empezar.
          </div>
        )}

        {isOffline && !dataLoading && (
          <OfflineBanner pendingCount={pendingCount} darkMode={darkMode} theme={theme} />
        )}

        {dataLoading && (
          <div className='flex justify-center py-10'>
            <div className='w-10 h-10 border-[3px] border-[#d97745] border-t-transparent rounded-full animate-spin' />
          </div>
        )}

        {!dataLoading && children}
        <Analytics />
      </div>
    </div>
  );
}
